<subscription-banner>
  <div if={ state.shouldShow } class="alert { getAlertClass() } shadow-lg mb-4 animate-slide-down subscription-banner-container">
    <div class="flex items-start gap-4 w-full">
      <!-- Icon based on subscription state -->
      <svg if={ state.isGracePeriod } xmlns="http://www.w3.org/2000/svg" class="shrink-0 h-7 w-7 mt-1" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <svg if={ state.isExpired } xmlns="http://www.w3.org/2000/svg" class="shrink-0 h-7 w-7 mt-1" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <svg if={ !state.isGracePeriod && !state.isExpired } xmlns="http://www.w3.org/2000/svg" class="shrink-0 h-7 w-7 mt-1" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      
      <!-- Message content -->
      <div class="flex-1">
        <div class="flex items-center gap-3 mb-2">
          <h3 class="font-bold text-lg">{ state.title }</h3>
          <!-- Countdown badge for grace period and trial -->
          <span if={ state.daysRemaining !== null && state.isGracePeriod }
                class="badge { getCountdownBadgeClass() } badge-lg font-bold">
            { state.countdownLabel }
          </span>
        </div>
        <p class="text-base leading-relaxed">{ state.message }</p>
        
        <!-- Progress bar for grace period -->
        <div if={ state.isGracePeriod && state.daysRemaining !== null } class="mt-3">
          <progress
            class="progress { getProgressClass() } w-full h-2"
            value={ state.daysRemaining }
            max={ state.graceTotalDays }>
          </progress>
          <p class="text-xs mt-1 opacity-75">
            {locale?.t('subscription_graceperiod_progress', { remaining: state.daysRemaining, total: state.graceTotalDays })}
          </p>
        </div>
      </div>
      
      <!-- Action buttons -->
      <div class="flex gap-2 mt-1">
        <a if={ shouldShowUpgradeButton() }
           href="#billing"
           class="btn btn-md { getPrimaryButtonClass() }">
          { state.primaryAction }
        </a>
        <button if={ state.canDismiss } onclick={ dismiss } class="btn btn-md btn-ghost">
          {locale?.t('dismiss')}
        </button>
      </div>
    </div>
  </div>

  <script>
    import { formatDistanceToNow, differenceInCalendarDays } from 'date-fns';
    import { getFeatureFlagService } from "../services/service-loader";
    import { NOTICE_KEYS, isDismissalActive, recordDismissal } from '../utils/subscriptionBannerDismissal.js';

    // Instance variable for synchronous access
    let featureFlagServiceInstance = null;

    const RENEWAL_LAG_TOLERANCE_HOURS = 24;

    export default {
      includeStore: true,

      state: {
        shouldShow: false,
        isGracePeriod: false,
        isExpired: false,
        isTrial: false,
        isRenewalSoon: false,
        title: '',
        message: '',
        primaryAction: '',
        canDismiss: false,
        daysRemaining: null,
        countdownLabel: '',
        graceTotalDays: 14,
        noticeKey: null
      },

      async onMounted() {
        console.log('[SubscriptionBanner] Component initializing...');

        // Load feature flag service for synchronous access
        featureFlagServiceInstance = await getFeatureFlagService();

        // The grace period length is env-overridable (DEFAULT_GRACE_PERIOD_DAYS),
        // so the progress bar cannot assume 14 days — an account on a different
        // length would be measured against the wrong total (METALARK-1202).
        // getSubscriptionConfig() is memoized, so this shares one request per
        // session with any admin page that has already asked for it.
        try {
          const { billingService } = await import('../api/billingService');
          const config = await billingService.getSubscriptionConfig({ skipLoading: true });
          const days = config?.lifecycle?.gracePeriodDays;
          if (typeof days === 'number' && days > 0) {
            this.state.graceTotalDays = days;
          }
        } catch (error) {
          console.warn('[SubscriptionBanner] Could not read grace period length, using default:', error);
        }

        // Subscribe to subscriptionStatus from the Redux store. App boot
        // (index.tag → initSubscriptionStatus) seeds the store, and the
        // 4-hour interval below is the only refresh path. This makes
        // subscription-banner and the upload page share one network call
        // per refresh cycle instead of each fetching independently.
        this.unsubscribe = this.store.subscribe(() => {
          const state = this.store.getState();
          const status = state.subscriptionStatus;
          const role = state.userContext?.role;
          // Re-evaluate when the status changes OR when the role first becomes
          // known (METALARK-1104): if an ambient PARTNER_ADMIN's userContext
          // loads after the subscription status, this re-runs applyStatus so the
          // never-applicable banner is suppressed rather than left showing.
          if (status !== this._lastStatus || role !== this._lastRole) {
            this._lastStatus = status;
            this._lastRole = role;
            this.applyStatus(status);
          }
        });

        const seeded = this.store.getState().subscriptionStatus;
        if (seeded) {
          this._lastStatus = seeded;
          this.applyStatus(seeded);
        } else {
          // Boot may not have populated the store yet (e.g. banner mounted on
          // a public route earlier in the session). Fall back to one fetch.
          const { tools } = await import('../services/tools');
          tools.fetchAndUpdateSubscriptionStatus();
        }

        // Store session start time for interval calculation
        this.sessionStartTime = Date.now();

        // Refresh subscription status every 4 hours for long-lived sessions.
        // 6 requests/day per user; result lands in the store and any subscriber
        // (this banner, upload page) re-renders.
        console.log('[SubscriptionBanner] Setting up 4-hour polling interval');
        this.interval = setInterval(async () => {
          console.log('[SubscriptionBanner] 4-hour interval triggered, refreshing subscription status...');
          const { tools } = await import('../services/tools');
          tools.fetchAndUpdateSubscriptionStatus();
        }, 4 * 60 * 60 * 1000); // 4 hours

        console.log('[SubscriptionBanner] Component initialized successfully');
      },

      onUnmounted() {
        console.log('[SubscriptionBanner] Component unmounting, clearing interval');
        if (this.interval) {
          clearInterval(this.interval);
        }
        if (this.unsubscribe) {
          this.unsubscribe();
        }
      },

      currentCustomerId() {
        return this.store.getState()?.userContext?.customerId;
      },

      isNoticeDismissed() {
        const isDismissed = isDismissalActive(this.state.noticeKey, this.currentCustomerId());
        console.log('[SubscriptionBanner] Dismissal check:', {
          noticeKey: this.state.noticeKey,
          canDismiss: this.state.canDismiss,
          isDismissed
        });
        return isDismissed;
      },

      // Render the banner from a status payload (store-sourced). Falls back
      // gracefully when store value is null (no subscription loaded yet).
      applyStatus(status) {
        if (!status) {
          this.state.shouldShow = false;
          this.update();
          return;
        }
        // METALARK-1104: an ambient PARTNER_ADMIN (the partner portal shell, not
        // yet entered into a customer) has no subscription of its own by design.
        // Its status read resolves to 'no_subscription', which would otherwise
        // render as a red "Subscription Expired" banner. Suppress the banner for
        // that context. Once the partner enters a customer the role is downgraded
        // to CUSTOMER_ADMIN, so a genuine expired banner still shows inside a real
        // customer workspace.
        const role = this.store.getState()?.userContext?.role;
        if (role === 'PARTNER_ADMIN') {
          this.state.shouldShow = false;
          this.update();
          return;
        }
        this.updateBannerState(status);
        if (this.state.canDismiss && this.isNoticeDismissed()) {
          this.state.shouldShow = false;
        }
        this.update();
        console.log('[SubscriptionBanner] Banner state updated from store:', {
          shouldShow: this.state.shouldShow,
          isGracePeriod: this.state.isGracePeriod,
          isExpired: this.state.isExpired,
          isTrial: this.state.isTrial,
          title: this.state.title
        });
      },

      updateBannerState(status) {
        console.log('[SubscriptionBanner] updateBannerState() called with status:', status.status);
        
        // Reset state
        this.state.isGracePeriod = false;
        this.state.isExpired = false;
        this.state.isTrial = false;
        this.state.isRenewalSoon = false;
        // daysRemaining was not reset here, so a count left over from a
        // previous status survived into notices that never set one — a
        // customer moving from grace period to past_due within a session got
        // a "-2 days left" pill and progress bar on the Payment Failed and
        // Subscription Canceled notices (METALARK-1202). Each handler sets
        // this where the notice genuinely has a countdown.
        this.state.daysRemaining = null;
        this.state.countdownLabel = '';
        this.state.noticeKey = null;
        
        const now = new Date();
        
        // Handle different subscription states
        console.log('[SubscriptionBanner] Routing to handler for status:', status.status);
        switch (status.status) {
          case 'trial':
          case 'trialing':
            this.handleTrialStatus(status, now);
            break;
            
          case 'canceled':
            this.handleCanceledStatus(status, now);
            break;
            
          case 'unpaid':
          case 'past_due':
            this.handlePastDueStatus(status, now);
            break;
            
          case 'active':
            this.handleActiveStatus(status, now);
            break;
            
          case 'expired':
          case 'no_subscription':
            this.handleExpiredStatus(status);
            break;
            
          default:
            this.state.shouldShow = false;
        }
      },

      handleTrialStatus(status, now) {
        console.log('[SubscriptionBanner] handleTrialStatus() - Trial status detected');
        
        if (status.trialEnd) {
          const trialEnd = new Date(status.trialEnd);
          const daysRemaining = differenceInCalendarDays(trialEnd, now);
          
          console.log('[SubscriptionBanner] Trial days remaining:', daysRemaining);
          
          if (daysRemaining <= 0) {
            // Trial ended - calculate dynamic days until deletion (trial end + 30 days)
            const deletionDate = new Date(trialEnd);
            deletionDate.setDate(deletionDate.getDate() + 30);
            const daysUntilDeletion = Math.max(0, differenceInCalendarDays(deletionDate, now));

            console.log('[SubscriptionBanner] Trial has ended, showing expired warning with deletion timeline', {
              trialEnd: trialEnd.toISOString(),
              deletionDate: deletionDate.toISOString(),
              daysUntilDeletion
            });

            this.state.shouldShow = true;
            this.state.isTrial = true;
            this.state.isExpired = true; // Mark as expired so we get the error styling
            this.state.daysRemaining = null; // Hide the countdown badge
            this.state.title = this.locale?.t('subscription_trial_ended_title') || 'Trial Ended';
            this.state.message = this.locale?.t('subscription_trial_ended_msg', { days: daysUntilDeletion }) || `Upgrade now to restore access. Your data will be permanently deleted in ${daysUntilDeletion} ${daysUntilDeletion === 1 ? 'day' : 'days'} if you do not subscribe.`;
            this.state.primaryAction = this.locale?.t('subscription_upgradenow') || 'Upgrade Now';
            this.state.noticeKey = NOTICE_KEYS.TRIAL_ENDED;
            this.state.canDismiss = false;
          } else if (daysRemaining <= 7) {
            // Trial ending soon - show countdown
            this.state.shouldShow = true;
            this.state.isTrial = true;
            this.state.daysRemaining = daysRemaining; // countdown badge now renders for grace-period only; kept for state consistency
            this.state.title = this.locale?.t('subscription_trial_ending_title', { days: daysRemaining }) || `Trial Ending in ${daysRemaining} ${daysRemaining === 1 ? 'Day' : 'Days'}`;
            this.state.message = this.locale?.t('subscription_trial_ending_msg') || 'Upgrade now to avoid interruption.';
            this.state.primaryAction = this.locale?.t('subscription_upgradenow') || 'Upgrade Now';
            this.state.noticeKey = NOTICE_KEYS.TRIAL_ENDING;
            this.state.canDismiss = daysRemaining > 3;
          } else {
            this.state.shouldShow = false;
          }
        }
      },

      handleCanceledStatus(status, now) {
        console.log('[SubscriptionBanner] handleCanceledStatus() - Canceled subscription detected');
        console.log('[SubscriptionBanner] gracePeriodEnd value:', status.gracePeriodEnd);
        
        if (status.gracePeriodEnd) {
          const gracePeriodEnd = new Date(status.gracePeriodEnd);
          const daysRemaining = differenceInCalendarDays(gracePeriodEnd, now);
          
          console.log('[SubscriptionBanner] Grace period calculation:', {
            gracePeriodEnd: gracePeriodEnd.toISOString(),
            now: now.toISOString(),
            daysRemaining,
            gracePeriodEndTime: gracePeriodEnd.getTime(),
            nowTime: now.getTime(),
            hasTimeLeft: gracePeriodEnd.getTime() > now.getTime()
          });
          
          this.state.daysRemaining = daysRemaining;
          
          // Check if grace period has actually expired (use actual time comparison)
          const hasTimeLeft = gracePeriodEnd.getTime() > now.getTime();
          
          if (!hasTimeLeft) {
            // Grace period truly expired (past the end time)
            console.log('[SubscriptionBanner] Grace period has expired (past end time)');
            this.handleExpiredStatus(status);
          } else if (daysRemaining === 0) {
            // Last day of grace period (expires today)
            console.log('[SubscriptionBanner] Last day of grace period (expires today)');
            this.state.shouldShow = true;
            this.state.isGracePeriod = true;
            // METALARK-1202: heading states the situation, the pill carries how
            // far through the grace period the account is — so neither the day
            // count nor "final day" is stated twice.
            this.state.title = this.locale?.t('subscription_grace_finalday_title') || 'Grace Period';
            this.state.message = this.locale?.t('subscription_grace_finalday_msg') || 'Renew now to restore full access and prevent data deletion.';
            this.state.primaryAction = this.locale?.t('subscription_renewnow') || 'Renew Now';
            this.state.noticeKey = NOTICE_KEYS.GRACE_FINAL_DAY;
            this.state.canDismiss = false;
            this.state.daysRemaining = 0;
            this.state.countdownLabel = this.locale?.t('subscription_grace_finalday_pill') || 'Final day';
          } else {
            // In grace period
            this.state.shouldShow = true;
            this.state.isGracePeriod = true;
            // METALARK-1202: the day count lives in the countdown pill and the
            // progress label, so the heading states the situation only rather
            // than stating the same number a third time.
            this.state.title = this.locale?.t('subscription_grace_remaining_title') || 'Grace Period';
            this.state.countdownLabel = this.locale?.t('subscription_daysleft', { count: daysRemaining }) || `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`;
            this.state.message = this.locale?.t('subscription_grace_remaining_msg') || 'Renew your subscription to restore full access and prevent data deletion. Until then your account is read-only.';
            this.state.primaryAction = this.locale?.t('subscription_renewsub') || 'Renew Subscription';
            this.state.noticeKey = NOTICE_KEYS.GRACE_PERIOD;
            this.state.canDismiss = false; // Grace period warnings cannot be dismissed
          }
        } else {
          // Canceled but grace period not set yet - show generic cancellation warning
          console.log('[SubscriptionBanner] Canceled subscription without gracePeriodEnd - showing generic warning');
          this.state.shouldShow = true;
          this.state.isGracePeriod = true;
          this.state.title = this.locale?.t('subscription_cancelled_title') || 'Subscription Canceled';
          this.state.message = this.locale?.t('subscription_cancelled_msg') || 'Renew your subscription to restore full access. Until then your account is read-only.';
          this.state.primaryAction = this.locale?.t('subscription_renewsub') || 'Renew Subscription';
          this.state.noticeKey = NOTICE_KEYS.SUBSCRIPTION_CANCELED;
          this.state.canDismiss = false;
        }
      },

      handlePastDueStatus(status, now) {
        console.log('[SubscriptionBanner] handlePastDueStatus() - Payment past due detected');
        this.state.shouldShow = true;
        this.state.isGracePeriod = true;
        this.state.title = this.locale?.t('subscription_paymentfailed_title') || 'Payment Failed';
        this.state.message = this.locale?.t('subscription_paymentfailed_msg') || 'Update your payment method to maintain access.';
        this.state.primaryAction = this.locale?.t('subscription_updatepayment') || 'Update Payment';
        this.state.noticeKey = NOTICE_KEYS.PAYMENT_FAILED;
        this.state.canDismiss = false;
      },

      handleActiveStatus(status, now) {
        console.log('[SubscriptionBanner] handleActiveStatus() - Active subscription');
        
        if (status.billingPeriodEnd) {
          const billingEnd = new Date(status.billingPeriodEnd);
          const daysUntilRenewal = differenceInCalendarDays(billingEnd, now);
          const hoursPastPeriodEnd = (now.getTime() - billingEnd.getTime()) / (1000 * 60 * 60);
          
          console.log('[SubscriptionBanner] Days until renewal:', daysUntilRenewal, 'cancelAtPeriodEnd:', status.cancelAtPeriodEnd);
          
          if (status.cancelAtPeriodEnd && daysUntilRenewal < 0) {
            console.log('[SubscriptionBanner] Scheduled cancellation has already taken effect');
            this.state.shouldShow = true;
            this.state.isRenewalSoon = true;
            this.state.title = this.locale?.t('subscription_ended_title') || 'Subscription Ended';
            this.state.message = this.locale?.t('subscription_ended_msg') || 'Reactivate now to restore full access.';
            this.state.primaryAction = this.locale?.t('subscription_reactivate') || 'Reactivate';
            this.state.noticeKey = NOTICE_KEYS.SUBSCRIPTION_ENDED;
            this.state.canDismiss = false;
          } else if (status.cancelAtPeriodEnd && daysUntilRenewal === 0) {
            console.log('[SubscriptionBanner] Scheduled cancellation takes effect today');
            this.state.shouldShow = true;
            this.state.isRenewalSoon = true;
            this.state.title = this.locale?.t('subscription_ending_today_title') || 'Subscription Ends Today';
            this.state.message = this.locale?.t('subscription_ending_today_msg') || 'Reactivate now to keep full access.';
            this.state.primaryAction = this.locale?.t('subscription_reactivate') || 'Reactivate';
            this.state.noticeKey = NOTICE_KEYS.SUBSCRIPTION_ENDING_TODAY;
            this.state.canDismiss = false;
          } else if (status.cancelAtPeriodEnd && daysUntilRenewal <= 14) {
            // Subscription set to cancel
            console.log('[SubscriptionBanner] Subscription set to cancel, showing ending warning');
            this.state.shouldShow = true;
            this.state.isRenewalSoon = true;
            this.state.title = this.locale?.t('subscription_ending_title') || 'Subscription Ending Soon';
            this.state.message = this.locale?.t('subscription_ending_msg', { days: daysUntilRenewal }) || `Your subscription will end in ${daysUntilRenewal} ${daysUntilRenewal === 1 ? 'day' : 'days'}. Renew now to continue uninterrupted access.`;
            this.state.primaryAction = this.locale?.t('subscription_reactivate') || 'Reactivate';
            this.state.noticeKey = NOTICE_KEYS.SUBSCRIPTION_ENDING_SOON;
            this.state.canDismiss = daysUntilRenewal > 7;
          } else if (!status.cancelAtPeriodEnd && (hoursPastPeriodEnd > RENEWAL_LAG_TOLERANCE_HOURS || status.canUploadResources === false)) {
            console.log('[SubscriptionBanner] Renewal did not complete', { hoursPastPeriodEnd, canUploadResources: status.canUploadResources });
            this.state.shouldShow = true;
            this.state.isGracePeriod = true;
            this.state.daysRemaining = null;
            this.state.title = this.locale?.t('subscription_renewalfailed_title') || 'Renewal Failed';
            this.state.message = this.locale?.t('subscription_renewalfailed_msg') || 'Check your billing details to restore full access.';
            this.state.primaryAction = this.locale?.t('subscription_checkbilling') || 'Check Billing';
            this.state.noticeKey = NOTICE_KEYS.RENEWAL_FAILED;
            this.state.canDismiss = false;
          } else if (daysUntilRenewal <= 7 && daysUntilRenewal > 0) {
            // Renewal coming up
            console.log('[SubscriptionBanner] Showing renewal reminder');
            this.state.shouldShow = true;
            this.state.isRenewalSoon = true;
            this.state.title = this.locale?.t('subscription_renewalin_title', { days: daysUntilRenewal }) || `Renewal in ${daysUntilRenewal} ${daysUntilRenewal === 1 ? 'Day' : 'Days'}`;
            this.state.message = this.locale?.t('subscription_renewalin_msg') || 'No action is needed. Review your billing details if your payment method has changed.';
            this.state.primaryAction = this.locale?.t('subscription_viewbilling') || 'View Billing';
            this.state.noticeKey = NOTICE_KEYS.RENEWAL_SOON;
            this.state.canDismiss = true;
          } else {
            console.log('[SubscriptionBanner] Active subscription, no warning needed (days until renewal: ' + daysUntilRenewal + ')');
            this.state.shouldShow = false;
          }
        }
      },

      handleExpiredStatus(status) {
        console.log('[SubscriptionBanner] handleExpiredStatus() - Subscription expired or no subscription');
        this.state.shouldShow = true;
        this.state.isExpired = true;

        // Distinguish between subscription expiration and grace period expiration
        if (status.status === 'canceled' && status.gracePeriodEnd) {
          // This is a grace period expiration (subscription was canceled, grace period ended)
          // Calculate dynamic days until deletion (grace end + 30 days)
          const gracePeriodEnd = new Date(status.gracePeriodEnd);
          const deletionDate = new Date(gracePeriodEnd);
          deletionDate.setDate(deletionDate.getDate() + 30);
          const daysUntilDeletion = Math.max(0, differenceInCalendarDays(deletionDate, new Date()));

          console.log('[SubscriptionBanner] Showing grace period expiration message with deletion timeline', {
            gracePeriodEnd: gracePeriodEnd.toISOString(),
            deletionDate: deletionDate.toISOString(),
            daysUntilDeletion
          });

          this.state.noticeKey = NOTICE_KEYS.GRACE_EXPIRED;
          this.state.title = this.locale?.t('subscription_grace_expired_title') || 'Grace Period Expired';
          this.state.message = this.locale?.t('subscription_grace_expired_msg', { days: daysUntilDeletion }) || `Renew immediately to restore access. Your data will be permanently deleted in ${daysUntilDeletion} ${daysUntilDeletion === 1 ? 'day' : 'days'}.`;
        } else {
          // This is a subscription expiration (trial ended, payment failed permanently, etc.)
          this.state.noticeKey = NOTICE_KEYS.SUBSCRIPTION_EXPIRED;
          this.state.title = this.locale?.t('subscription_expired_title') || 'Subscription Expired';
          this.state.message = this.locale?.t('subscription_expired_msg') || 'Renew now to continue accessing your data.';
          console.log('[SubscriptionBanner] Showing subscription expiration message');
        }

        this.state.primaryAction = this.locale?.t('subscription_renewnow') || 'Renew Now';
        this.state.canDismiss = false;
      },

      getAlertClass() {
        if (this.state.isExpired) {
          return 'alert-error';
        } else if (this.state.isGracePeriod) {
          return 'alert-warning';
        } else if (this.state.isTrial) {
          return 'alert-info';
        } else {
          return 'alert-info';
        }
      },

      getPrimaryButtonClass() {
        if (this.state.isExpired || this.state.isGracePeriod) {
          return 'btn-error';
        } else {
          return 'btn-primary';
        }
      },

      getCountdownBadgeClass() {
        if (this.state.daysRemaining <= 3) {
          return 'badge-error';
        } else if (this.state.daysRemaining <= 7) {
          return 'badge-warning';
        } else {
          return 'badge-info';
        }
      },

      getProgressClass() {
        if (this.state.daysRemaining <= 3) {
          return 'progress-error';
        } else if (this.state.daysRemaining <= 7) {
          return 'progress-warning';
        } else {
          return 'progress-success';
        }
      },

      shouldShowUpgradeButton() {
        // Check feature flag to determine if upgrade button should be shown
        // This allows hiding the button during testing/development
        const isEnabled = featureFlagServiceInstance?.isEnabledSync('subscription_upgrade_button');

        // If flag is not loaded yet or is explicitly disabled, hide button
        // If flag is enabled or not defined (fail open), show button
        if (isEnabled === false) {
          console.log('[SubscriptionBanner] Upgrade button hidden by feature flag');
          return false;
        }

        return true;
      },

      dismiss() {
        console.log('[SubscriptionBanner] User dismissed banner');

        const dismissData = recordDismissal(this.state.noticeKey, this.currentCustomerId());
        console.log('[SubscriptionBanner] Stored dismissal:', dismissData);

        this.state.shouldShow = false;
        this.update();
        console.log('[SubscriptionBanner] Banner hidden');
      }
    }
  </script>

  <style>
    :host {
      display: block;
      position: sticky;
      top: 0;
      z-index: 40;
    }

    /* Improved contrast for warning alerts */
    .subscription-banner-container.alert-warning {
      background-color: #fef3c7; /* Light yellow background */
      border-color: #f59e0b; /* Amber border */
      color: #78350f; /* Dark brown text for better contrast */
    }

    .subscription-banner-container.alert-warning svg {
      stroke: #d97706; /* Darker amber for icon */
    }

    /* Error state with good contrast */
    .subscription-banner-container.alert-error {
      background-color: #fee2e2; /* Light red background */
      border-color: #ef4444; /* Red border */
      color: #7f1d1d; /* Dark red text */
    }

    .subscription-banner-container.alert-error svg {
      stroke: #dc2626; /* Darker red for icon */
    }

    /* Info state with good contrast */
    .subscription-banner-container.alert-info {
      background-color: #dbeafe; /* Light blue background */
      border-color: #3b82f6; /* Blue border */
      color: #1e3a8a; /* Dark blue text */
    }

    .subscription-banner-container.alert-info svg {
      stroke: #2563eb; /* Darker blue for icon */
    }

    /* Ensure buttons have good contrast too */
    .subscription-banner-container .btn {
      font-weight: 600;
    }

    @keyframes slide-down {
      from {
        transform: translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .animate-slide-down {
      animation: slide-down 0.3s ease-out;
    }
  </style>
</subscription-banner>